import {
  Component,
  computed,
  EventEmitter,
  Input,
  input,
  OnDestroy,
  Output,
  signal,
} from "@angular/core";
import {
  ControlContainer,
  FormArray,
  FormControl,
  FormGroup,
  NgForm,
  Validators,
} from "@angular/forms";
import moment from "moment";
import { Subscription } from "rxjs";
import { FormValidators } from "src/app/common/validators/form-validators";
import LocalizationConstants from "src/app/constants/localization-constants";
import CustomerSettings from "src/app/models/CustomerSettings";
import { UnitOfMeasure } from "src/app/models/Enums";
import { FileToUpload } from "src/app/models/FileToUpload";
import { ParcelOption } from "src/app/models/ParcelDimensionsOption";
import { FormKeys, ServiceRequest } from "src/app/models/ServiceRequest";
import { DateUtils } from "src/app/utils/date.utils";
import { splitTime } from "src/app/utils/time.utils";

@Component({
  selector: "app-service-request-step-two",
  templateUrl: "./service-request-step-two.component.html",
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  standalone: false,
})
export class ServiceRequestStepTwoComponent implements OnDestroy {
  @Input() isRaResultReceived: boolean;
  @Input() poRequired: boolean;
  @Input() ra: ServiceRequest;
  @Input() step2form: FormGroup;
  @Input() customerSettings: CustomerSettings;
  @Input() shippingParcelsFormArray: FormArray;
  @Input()
  set isShipmentRequested(value: boolean) {
    this.shipmentRequested.set(value);
    this.setShippingParcelsForm(value);
    this.setPhoneNumberFormControl(value);
  }

  showValidationErrors = input<boolean>(false);

  @Output() attachDocument = new EventEmitter<FileToUpload>();

  private _subscriptions$: Subscription[] = [];

  formKeys = FormKeys;

  localizationConstants = LocalizationConstants;

  parcelsAdditionalInfoControl: FormControl<string>;
  pickupDateControl: FormControl<string>;
  businessStartControl: FormControl<string>;
  businessEndControl: FormControl<string>;
  totalParcelsControl: FormControl<number>;
  shipmentRequested = signal(false);

  // Show message to user to make sure phone is valid if shipping was requested and carrier is UPS
  readonly checkYourPhoneCarriers = ["UPS"];
  isCheckYourPhoneCarrier = computed(() =>
    this.checkYourPhoneCarriers.includes(this.customerSettings?.carrier),
  );

  phoneNumberControl: FormControl<string> = new FormControl<string>("", [Validators.required]);

  setPhoneNumberFormControl(isShipmentRequested: boolean) {
    // Add extra validation for specific Carriers when shipment is requested
    if (isShipmentRequested && this.isCheckYourPhoneCarrier()) {
      // Add validation on "+" at the beginning, min length should be 9 and max 16 including "+"
      this.phoneNumberControl.addValidators([
        FormValidators.isValidInternationalPhoneNumber(),
        Validators.minLength(9),
        Validators.maxLength(16),
      ]);
    }
    const existingControl = this.step2form.get(FormKeys.phoneNumber);
    // Add formControl into the step2form unless already there
    if (!existingControl) {
      this.step2form.addControl(FormKeys.phoneNumber, this.phoneNumberControl);
      // Register phoneNumber change into ra object
      const subscription = this.phoneNumberControl.valueChanges.subscribe((value) => {
        if (!this.ra) return;
        this.ra.phoneNumber = value;
      });
      this._subscriptions$.push(subscription);
    }
    // Set initial value from ra phoneNumber
    this.phoneNumberControl.setValue(this.ra?.phoneNumber);
  }

  setShippingParcelsForm(isShipmentRequested: boolean = false) {
    if (!this.customerSettings?.isShippingEnabled) return;

    if (isShipmentRequested) {
      this.shippingParcelsFormArray = new FormArray<FormGroup>([]);
      this.addPackage();

      this.step2form.addControl(FormKeys.shippingParcels, this.shippingParcelsFormArray);

      this.totalParcelsControl = new FormControl<number>(1, [Validators.min(1)]);
      this.step2form.addControl(FormKeys.totalParcels, this.totalParcelsControl);

      this.parcelsAdditionalInfoControl = new FormControl<string>("");
      this.step2form.addControl(FormKeys.parcelsAdditionalInfo, this.parcelsAdditionalInfoControl);

      this.pickupDateControl = new FormControl<string>(
        DateUtils.getDatePart(this.getDefaultPickupDate()),
        [Validators.required],
      );
      this.step2form.addControl(FormKeys.pickupDate, this.pickupDateControl);

      // Temporary hardcoding time due to errors with time validation on Montova/UPS side
      this.businessStartControl = new FormControl<string>("09:00", [
        Validators.required,
        (control) => {
          const startHoursSplit = splitTime(control.value);
          const endHoursSplit = splitTime(this.businessEndControl?.value);
          if (!startHoursSplit || !endHoursSplit) return null;
          if (startHoursSplit.hours > endHoursSplit.hours)
            return { invalidTime: LocalizationConstants.LABEL_START_TIME_MUST_BE_BEFORE_END_TIME };
          return null;
        },
      ]);

      // this.businessStartControl = new FormControl<string>("09:00", [
      //   Validators.required,
      //   (control) => {
      //     const startHoursSplit = splitTime(control.value);
      //     const endHoursSplit = splitTime(this.businessEndControl?.value);
      //     if (!startHoursSplit || !endHoursSplit) return null;
      //     if (startHoursSplit.hours > endHoursSplit.hours)
      //       return { invalidTime: LocalizationConstants.LABEL_START_TIME_MUST_BE_BEFORE_END_TIME };
      //     return null;
      //   },
      // ]);
      this.step2form.addControl(FormKeys.businessStart, this.businessStartControl);

      // Temporary hardcoding time due to errors with time validation on Montova/UPS side
      this.businessEndControl = new FormControl<string>("16:00", [
        Validators.required,
        (control) => {
          const startHoursSplit = splitTime(this.businessStartControl?.value);
          const endHoursSplit = splitTime(control.value);
          if (!startHoursSplit || !endHoursSplit) return null;
          if (startHoursSplit.hours > endHoursSplit.hours)
            return { invalidTime: LocalizationConstants.LABEL_START_TIME_MUST_BE_BEFORE_END_TIME };
          return null;
        },
      ]);

      // this.businessEndControl = new FormControl<string>("17:00", [
      //   Validators.required,
      //   (control) => {
      //     const startHoursSplit = splitTime(this.businessStartControl?.value);
      //     const endHoursSplit = splitTime(control.value);
      //     if (!startHoursSplit || !endHoursSplit) return null;
      //     if (startHoursSplit.hours > endHoursSplit.hours)
      //       return { invalidTime: LocalizationConstants.LABEL_START_TIME_MUST_BE_BEFORE_END_TIME };
      //     return null;
      //   },
      // ]);
      this.step2form.addControl(FormKeys.businessEnd, this.businessEndControl);

      this._subscriptions$.push(
        this.shippingParcelsFormArray.valueChanges.subscribe((value) => {
          this.step2form.get(FormKeys.totalParcels).setValue(value.length);
        }),
      );
    } else {
      this.shippingParcelsFormArray = undefined;

      this.step2form.removeControl(FormKeys.shippingParcels);

      this.step2form.removeControl(FormKeys.totalParcels);
      this.totalParcelsControl = undefined;

      this.step2form.removeControl(FormKeys.parcelsAdditionalInfo);
      this.parcelsAdditionalInfoControl = undefined;

      this.step2form.removeControl(FormKeys.pickupDate);
      this.pickupDateControl = undefined;

      this.step2form.removeControl(FormKeys.businessStart);
      this.businessStartControl = undefined;

      this.step2form.removeControl(FormKeys.businessEnd);
      this.businessEndControl = undefined;
    }
  }

  addPackage() {
    this.shippingParcelsFormArray.push(this.createPackageForm());
  }

  removePackage(index: number) {
    this.shippingParcelsFormArray.removeAt(index);
  }

  copyPackage(parcel: ParcelOption) {
    this.shippingParcelsFormArray.push(this.createPackageForm(parcel));
  }

  get getMaxWeight() {
    return this.customerSettings.unitOfMeasure === UnitOfMeasure.Imperial ? 154 : 70;
  }

  createPackageForm(parcel?: ParcelOption): FormGroup {
    return new FormGroup({
      [FormKeys.parcelType]: new FormControl<string>(parcel?.parcelType, [Validators.required]),
      [FormKeys.weight]: new FormControl<number>(parcel?.weight, [
        Validators.required,
        Validators.min(1),
        Validators.max(this.getMaxWeight),
        FormValidators.isValidIntegerPattern(),
      ]),
      [FormKeys.description]: new FormControl<string>(parcel?.description, [
        // 35 is max for UPS, add logic if other carriers are used
        Validators.maxLength(35),
      ]),
    });
  }

  /**
   * When the pickup date changes, set the date part of the pickup date.
   */
  onPickupDateChange(date: Date) {
    this.pickupDateControl.setValue(DateUtils.getDatePart(date));
  }

  filterOutPastAndWeekends(date: Date | null): boolean {
    const dateTime = date || new Date();
    const isDateToday = moment(dateTime).isSame(moment(), "date");
    const todayNoon = moment().startOf("day").set("hour", 12).toDate();

    // If day is today and is before noon, allow today to be selected.
    const isTodayUntilNoon = !isDateToday || moment().toDate() <= todayNoon;

    const isInPast = dateTime < moment().startOf("day").toDate();

    const day = dateTime.getDay();
    const isSaturdayOrSunday = day === 0 || day === 6;

    // Prevent Saturday and Sunday and the past days from being selected.
    return !isInPast && isTodayUntilNoon && !isSaturdayOrSunday;
  }

  /**
   * If the current time is before noon, the default pickup date is today.
   * Otherwise, the default pickup date is next working day.
   */
  getDefaultPickupDate(): Date {
    const today = new Date();
    const todayNoon = moment().startOf("day").set("hour", 12).toDate();
    const dayOfWeekForToday = today.getDay();
    // 0 is Sunday, 6 is Saturday.
    if (today < todayNoon && dayOfWeekForToday !== 0 && dayOfWeekForToday !== 6) {
      return today;
    }

    // Get next working day based on dayOfWeekForToday
    // If today is Friday after noon, the next working day is after 3 days.
    // If today is Saturday, the next working day is after 2 days.
    // Otherwise, the next working day
    const numberOfDaysUntilNextWorkingDay =
      dayOfWeekForToday === 5 ? 3 : dayOfWeekForToday === 6 ? 2 : 1;

    return moment().add(numberOfDaysUntilNextWorkingDay, "days").toDate();
  }

  public onCommentsChange() {
    this.ra.commentsToShow = this.ra.comments;
  }

  ngOnDestroy() {
    this._subscriptions$.forEach((s) => s.unsubscribe());
  }
}
